import { Map as MlMap, SymbolLayerSpecification } from "maplibre-gl";

type Visibility = NonNullable<NonNullable<SymbolLayerSpecification["layout"]>["visibility"]>;
type Motion = "rotation" | "zoom";
const groups = new WeakMap<MlMap, RotationLabels>();
const isAirspaceLabel = (id: string): boolean => id === "vm-airspace-labels" || id === "vm-airspace-curved-labels";

/** Hide line labels on rotation and all airspace labels on rotation or zoom. */
class RotationLabels {
  private targets = new Map<MlMap, Map<string, Visibility>>();
  private hidden = { rotation: false, zoom: false };
  private timers: Partial<Record<Motion, ReturnType<typeof setTimeout>>> = {};

  constructor(private camera: MlMap) {
    camera.on("rotatestart", this.rotationStart);
    camera.on("rotateend", this.rotationEnd);
    camera.on("zoomstart", this.zoomStart);
    camera.on("zoomend", this.zoomEnd);
    camera.on("remove", this.dispose);
  }

  add(target: MlMap): () => void {
    const visibility = new Map<string, Visibility>();
    const originals = this.targets.get(this.camera);
    for (const layer of target.getStyle().layers) {
      if (layer.type !== "symbol" || !layer.id.startsWith("vm-")) continue;
      const placement = layer.layout?.["symbol-placement"];
      if (!isAirspaceLabel(layer.id) && placement !== "line" && placement !== "line-center") continue;
      // New planes can copy the temporarily hidden main style during motion.
      visibility.set(layer.id, originals?.get(layer.id) ?? layer.layout?.visibility ?? "visible");
    }
    this.targets.set(target, visibility);
    this.apply(target, visibility);
    return () => this.targets.delete(target);
  }

  private apply(target: MlMap, visibility: Map<string, Visibility>): void {
    for (const [id, original] of visibility) {
      const hidden = this.hidden.rotation || (this.hidden.zoom && isAirspaceLabel(id));
      const value = hidden ? "none" : original;
      if (target.getLayer(id) && target.getLayoutProperty(id, "visibility") !== value) {
        target.setLayoutProperty(id, "visibility", value);
      }
    }
  }

  private update(): void {
    for (const [target, visibility] of this.targets) this.apply(target, visibility);
  }

  private start(motion: Motion): void {
    clearTimeout(this.timers[motion]);
    if (this.hidden[motion]) return;
    this.hidden[motion] = true;
    this.update();
  }

  private end(motion: Motion): void {
    clearTimeout(this.timers[motion]);
    // Held controls emit end events each frame. Wait for a quiet interval.
    this.timers[motion] = setTimeout(() => {
      this.hidden[motion] = false;
      this.update();
    }, 150);
  }

  private rotationStart = (): void => this.start("rotation");
  private rotationEnd = (): void => this.end("rotation");
  private zoomStart = (): void => this.start("zoom");
  private zoomEnd = (): void => this.end("zoom");

  private dispose = (): void => {
    clearTimeout(this.timers.rotation);
    clearTimeout(this.timers.zoom);
    this.camera.off("rotatestart", this.rotationStart);
    this.camera.off("rotateend", this.rotationEnd);
    this.camera.off("zoomstart", this.zoomStart);
    this.camera.off("zoomend", this.zoomEnd);
    this.camera.off("remove", this.dispose);
    this.targets.clear();
    groups.delete(this.camera);
  };
}

export function hideVectorLabelsDuringRotation(camera: MlMap, target = camera): () => void {
  let group = groups.get(camera);
  if (!group) {
    group = new RotationLabels(camera);
    groups.set(camera, group);
  }
  return group.add(target);
}
